import React from 'react';
import { useMutation } from '@apollo/client';
import { ModalForm } from '../Modal/ModalForm';
import { UserAttributeEditor } from '../user/UserAttributeEditor';
import { UPDATE_USER } from '../../utils/graphql/users/mutations';

function getAttributeMap(attributes) {
  const map = {};
  if (!Array.isArray(attributes)) return map;

  attributes.forEach((attr) => {
    if (!attr || typeof attr.key !== 'string') return;
    map[attr.key] = attr.value;
  });

  return map;
}

export default function OnboardingWizardModal({
  user,
  onboarding,
  onComplete,
  onSkip,
}) {
  const missingProfileFields = React.useMemo(
    () => onboarding?.missingProfileFields ?? [],
    [onboarding?.missingProfileFields]
  );
  const missingAttributeKeys = React.useMemo(
    () => onboarding?.missingAttributeKeys ?? [],
    [onboarding?.missingAttributeKeys]
  );

  const steps = React.useMemo(() => {
    const nextSteps = [];
    if (missingProfileFields.length > 0) nextSteps.push('profile');
    if (missingAttributeKeys.length > 0) nextSteps.push('attributes');
    if (nextSteps.length === 0) nextSteps.push('review');
    return nextSteps;
  }, [missingProfileFields, missingAttributeKeys]);

  const [stepIndex, setStepIndex] = React.useState(0);
  const [timezone, setTimezone] = React.useState(user?.timezone ?? '');
  const [savingTimezone, setSavingTimezone] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [attributeValues, setAttributeValues] = React.useState(() => getAttributeMap(user?.attributes));

  React.useEffect(() => {
    setStepIndex(0);
    setTimezone(user?.timezone ?? '');
    setAttributeValues(getAttributeMap(user?.attributes));
    setSaveError('');
  }, [user?._id, user?.timezone, user?.attributes]);

  const [updateUser, { loading: saving }] = useMutation(UPDATE_USER);

  const currentStep = steps[stepIndex];
  const isLastStep = stepIndex >= steps.length - 1;

  const handleDetectTimezone = () => {
    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!detected) return;

    setSavingTimezone(true);
    setTimezone(detected);
    setSavingTimezone(false);
  };

  const handleSave = async () => {
    setSaveError('');

    const mergedAttrMap = new Map(
      Array.isArray(user?.attributes)
        ? user.attributes.map((attr) => [attr.key, attr.value])
        : []
    );

    Object.entries(attributeValues).forEach(([key, value]) => {
      mergedAttrMap.set(key, value);
    });

    const attributes = Array.from(mergedAttrMap.entries()).map(([key, value]) => ({ key, value }));

    try {
      await updateUser({
        variables: {
          _id: user._id,
          timezone,
          attributes,
        },
      });
      onComplete?.();
    } catch (err) {
      setSaveError(err?.message ?? 'Failed to save onboarding updates.');
    }
  };

  const renderProfileStep = () => (
    <div>
      <p className="mb-3 text-muted">Please complete the missing profile details so job matching stays accurate.</p>

      {missingProfileFields.includes('timezone') && (
        <div className="form-group mb-3">
          <label htmlFor="onboarding-timezone">Timezone</label>
          <div className="d-flex gap-2">
            <input
              id="onboarding-timezone"
              type="text"
              className="form-control"
              placeholder="America/New_York"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline-primary flex-shrink-0"
              onClick={handleDetectTimezone}
              disabled={savingTimezone}
              title="Detect timezone from this browser"
            >
              Detect
            </button>
          </div>
          <small className="text-muted">Timezone improves fairness for response-time scoring across regions.</small>
        </div>
      )}
    </div>
  );

  const renderAttributesStep = () => (
    <div>
      <p className="mb-3 text-muted">A few profile attributes are missing. These help route jobs to the right substitutes.</p>
      <UserAttributeEditor
        initialValues={attributeValues}
        onChange={(nextValues) => setAttributeValues((prev) => ({ ...prev, ...nextValues }))}
        onlyEditable
        includeKeys={missingAttributeKeys}
        title="Missing attributes"
      />
    </div>
  );

  const renderReviewStep = () => (
    <p className="mb-0 text-muted">
      Your profile fields are already filled, but your onboarding schema version is behind the current system version.
      Click Save and finish to sync your account and prevent repeat prompts.
    </p>
  );

  const footer = (
    <>
      <button type="button" className="btn btn-outline-secondary me-auto" onClick={onSkip}>
        Not now
      </button>
      {stepIndex > 0 && (
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => setStepIndex((idx) => Math.max(0, idx - 1))}
        >
          Back
        </button>
      )}
      {!isLastStep && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setStepIndex((idx) => Math.min(steps.length - 1, idx + 1))}
        >
          Continue
        </button>
      )}
      {isLastStep && (
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? 'Saving...' : 'Save and finish'}
        </button>
      )}
    </>
  );

  return (
    <ModalForm
      title={`Finish your profile (${stepIndex + 1}/${steps.length})`}
      onClose={onSkip}
      footer={footer}
      size="lg"
    >
      {saveError ? <div className="alert alert-danger">{saveError}</div> : null}
      {currentStep === 'profile' ? renderProfileStep() : null}
      {currentStep === 'attributes' ? renderAttributesStep() : null}
      {currentStep === 'review' ? renderReviewStep() : null}
    </ModalForm>
  );
}
